import {
    CircularProgress,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import {
    useAiActivateSmartReply,
    useAiSmartReplyPlans,
} from '../../../../sic-lib/api/hooks/useAiSmartReply';
import { Contact } from '../../../../sic-lib/types/Contact';
import AxiosErrorAlert from '../../../Shared/Alerts/AxiosErrorAlert';
import ButtonLoading from '../../../Shared/ButtonLoading';

interface Props {
    contact: Contact;
    isUserAdmin: boolean;
    userName: string;
}

export function SmartReplySelectPlan({ contact, isUserAdmin, userName }: Props) {
    const { data: availablePlans, isLoading: isLoadingAvailablePlans } = useAiSmartReplyPlans();
    const {
        mutate: handleActivateSmartReply,
        isLoading: isActivatingSmartReply,
        isSuccess: isActivatingSmartReplySuccess,
        isError: isActivatingSmartReplyError,
        error: updateError,
    } = useAiActivateSmartReply();

    const [selectedPlanId, setSelectedPlanId] = useState<number>(0);
    const [selectedUserName, setSelectedUserName] = useState<string>('');

    const userNames = useMemo(() => {
        return isUserAdmin
            ? contact.owners.map(owner => owner.userName)
            : [contact.owners.find(owner => owner.userName === userName)?.userName];
    }, [contact.owners, isUserAdmin, userName]);

    useEffect(() => {
        if (contact.owners?.length) {
            const primaryOwner = contact.owners.find(owner => owner.isPrimary);
            if (primaryOwner) {
                setSelectedUserName(primaryOwner.userName);
            }
        }
    }, [contact.owners]);

    const handleSelectPlan = (event: SelectChangeEvent<number>) => {
        setSelectedPlanId(event.target.value as number);
    };

    const handleSelectUser = (event: SelectChangeEvent<string>) => {
        setSelectedUserName(event.target.value as string);
    };

    if (isLoadingAvailablePlans) {
        return (
            <Grid item>
                <CircularProgress />
            </Grid>
        );
    }

    return (
        <>
            <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="smart-reply-contact-plan">Automatic Smart Reply Plan</InputLabel>
                <Select
                    id="smart-reply-contact-plan"
                    label="Automatic Smart Reply Plan"
                    value={selectedPlanId}
                    onChange={handleSelectPlan}
                >
                    {availablePlans?.map(plan => (
                        <MenuItem key={plan.planId} value={plan.planId}>
                            {plan.name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <FormControl fullWidth>
                <InputLabel id="smart-reply-contact-plan-username">
                    Send/Reply UserName (from Contact User Owner & Followers)
                </InputLabel>
                <Select
                    id="smart-reply-contact-plan-username"
                    label="Send/Reply UserName (from Contact User Owner & Followers)"
                    value={selectedUserName}
                    onChange={handleSelectUser}
                >
                    {userNames?.map(userName => (
                        <MenuItem key={userName} value={userName}>
                            {userName}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <ButtonLoading
                isLoading={isActivatingSmartReply}
                isError={isActivatingSmartReplyError}
                isSuccess={isActivatingSmartReplySuccess}
                disabled={selectedPlanId === 0}
                successLabel="Activated"
                fullWidth
                variant="contained"
                color="primary"
                onClick={() =>
                    handleActivateSmartReply({
                        contactId: contact.contactId,
                        planId: selectedPlanId!,
                        channelIndexId: undefined,
                        userName: selectedUserName,
                    })
                }
            >
                Activate Automatic Smart Reply
            </ButtonLoading>
            <AxiosErrorAlert error={updateError} />
        </>
    );
}
